import React, { useState } from 'react';
import './BankReconciliation.css';
import { API_BASE_URL, authFetch } from '../../config/api.config';

/**
 * Bankzahlungsabgleich, Erststufe (Anlage v1.4 Abschnitt 2.5 Punkt 10, Erststufe CSV):
 * CSV-Import von Kontoumsätzen (Datei-Upload oder eingefügter Text), Abgleich mit
 * offenen Rechnungen und manuelle Bestätigung der Vorschläge durch die Buchhaltung.
 * Kein Komfortkasse-API-Anschluss in dieser Stufe.
 */

interface BankTransaction {
  date?: string | null;
  amount: number;
  purpose: string;
  name: string;
  isCredit: boolean;
}

interface BankMatchSuggestion {
  invoiceId: number;
  invoiceNumber: string;
  bookingNumber: string;
  customerName: string;
  amountDue: number;
  totalAmount: number;
}

interface BankTransactionMatch {
  transaction: BankTransaction;
  confidence: string; // 'hoch' | 'mittel' | 'niedrig' | 'kein Treffer' | 'ignoriert'
  reason: string;
  suggestion?: BankMatchSuggestion | null;
}

interface BankReconciliationSummary {
  totalTransactions: number;
  creditTransactions: number;
  debitTransactions: number;
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
  noMatch: number;
}

interface BankReconciliationResult {
  summary: BankReconciliationSummary;
  results: BankTransactionMatch[];
}

const BankReconciliation: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [result, setResult] = useState<BankReconciliationResult | null>(null);
  const [confirmedRows, setConfirmedRows] = useState<Set<number>>(new Set());
  const [confirmingRow, setConfirmingRow] = useState<number | null>(null);
  // Stufe 2: manuelle Zuordnung bei "kein Treffer" (Liste offener Rechnungen plus Auswahl je Zeile)
  const [openInvoices, setOpenInvoices] = useState<BankMatchSuggestion[] | null>(null);
  const [manualSelection, setManualSelection] = useState<{ [rowIndex: number]: number }>({});

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const truncate = (text: string, maxLength: number): string => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '…' : text;
  };

  const confidenceBadgeClass = (confidence: string): string => {
    switch (confidence) {
      case 'hoch':
        return 'bankrec-badge bankrec-badge-high';
      case 'mittel':
        return 'bankrec-badge bankrec-badge-medium';
      case 'niedrig':
        return 'bankrec-badge bankrec-badge-low';
      default:
        return 'bankrec-badge bankrec-badge-none';
    }
  };

  const confidenceLabel = (row: BankTransactionMatch): string => {
    if (row.confidence === 'ignoriert') return 'Lastschrift';
    return row.confidence;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
    setFile(selected);
  };

  const handleReconcile = async () => {
    setError(null);
    setSuccessMessage(null);

    const hasFile = file !== null;
    const hasText = csvText.trim().length > 0;
    if (!hasFile && !hasText) {
      setError('Bitte eine CSV-Datei auswählen oder Kontoumsätze als Text einfügen.');
      return;
    }

    try {
      setLoading(true);

      let response: Response;
      if (hasFile) {
        // Datei als multipart hochladen, damit das Backend das Encoding tolerant erkennt
        const formData = new FormData();
        formData.append('file', file as File);
        // T-009: authFetch entfernt bei FormData den Content-Type-Header selbst
        // (multipart-Boundary setzt der Browser) und refresht bei 401 automatisch
        response = await authFetch(`${API_BASE_URL}/bank-reconciliation/import`, {
          method: 'POST',
          body: formData
        });
      } else {
        response = await authFetch(`${API_BASE_URL}/bank-reconciliation/import`, {
          method: 'POST',
          body: JSON.stringify({ csv: csvText })
        });
      }

      if (!response.ok) {
        let message = `HTTP ${response.status}`;
        try {
          const data = await response.json();
          if (data && data.error) message = data.error;
        } catch {
          // Antwort ohne JSON-Body, Standardmeldung verwenden
        }
        throw new Error(message);
      }

      const data: BankReconciliationResult = await response.json();
      setResult(data);
      setConfirmedRows(new Set());
      setManualSelection({});

      // Stufe 2: offene Rechnungen fuer die manuelle Zuordnung nachladen (best effort)
      loadOpenInvoices();
    } catch (err: any) {
      setError(`Fehler beim Abgleich: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Stufe 2: Liste offener Rechnungen fuer die manuelle Zuordnung
  const loadOpenInvoices = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/bank-reconciliation/open-invoices`, {});
      if (response.ok) {
        const data: BankMatchSuggestion[] = await response.json();
        setOpenInvoices(data);
      }
    } catch {
      // Manuelle Zuordnung ist dann nicht verfuegbar, Abgleich funktioniert trotzdem
      setOpenInvoices(null);
    }
  };

  /**
   * Verbucht eine Zahlung. Stufe 2: Bei 409 (Doppel-Verbuchung oder Ueberzahlung)
   * wird die Schutzmeldung angezeigt und nach ausdruecklicher Bestaetigung mit
   * force=true wiederholt.
   */
  const submitConfirm = async (invoiceId: number, row: BankTransactionMatch, force: boolean): Promise<Response> => {
    return authFetch(`${API_BASE_URL}/bank-reconciliation/confirm`, {
      method: 'POST',
      body: JSON.stringify({
        invoiceId,
        amount: row.transaction.amount,
        paymentDate: row.transaction.date ?? null,
        reference: row.transaction.purpose,
        force
      })
    });
  };

  const handleConfirmPayment = async (rowIndex: number, row: BankTransactionMatch, manualInvoice?: BankMatchSuggestion) => {
    const target = manualInvoice ?? row.suggestion;
    if (!target) return;

    const confirmText =
      `Zahlung über ${formatCurrency(row.transaction.amount)} auf Rechnung ${target.invoiceNumber}` +
      ` (${target.customerName || 'Kunde unbekannt'})${manualInvoice ? ' MANUELL zugeordnet' : ''} verbuchen?`;
    if (!window.confirm(confirmText)) return;

    try {
      setConfirmingRow(rowIndex);
      setError(null);

      let response = await submitConfirm(target.invoiceId, row, false);

      // Stufe 2: Schutz hat gegriffen, Rueckfrage und ggf. force-Wiederholung
      if (response.status === 409) {
        let protectionMessage = 'Die Verbuchung wurde durch eine Schutzpruefung angehalten.';
        try {
          const data = await response.json();
          if (data && data.error) protectionMessage = data.error;
        } catch {
          // Standardmeldung verwenden
        }
        const proceed = window.confirm(`${protectionMessage}\n\nTrotzdem verbuchen?`);
        if (!proceed) return;
        response = await submitConfirm(target.invoiceId, row, true);
      }

      if (!response.ok) {
        let message = `HTTP ${response.status}`;
        try {
          const data = await response.json();
          if (data && data.error) message = data.error;
        } catch {
          // Antwort ohne JSON-Body, Standardmeldung verwenden
        }
        throw new Error(message);
      }

      const data = await response.json();
      const statusLabel = data.status === 'Paid' ? 'Bezahlt' : data.status === 'PartiallyPaid' ? 'Teilweise bezahlt' : data.status;
      setSuccessMessage(`Zahlung auf Rechnung ${data.invoiceNumber} verbucht, neuer Status: ${statusLabel}`);
      setConfirmedRows((previous) => {
        const next = new Set(previous);
        next.add(rowIndex);
        return next;
      });
    } catch (err: any) {
      setError(`Fehler beim Verbuchen der Zahlung: ${err.message}`);
    } finally {
      setConfirmingRow(null);
    }
  };

  return (
    <div className="bankrec-page">
      <div className="bankrec-header">
        <h1>Bankabgleich</h1>
      </div>

      <p className="bankrec-intro">
        Kontoumsätze als CSV importieren und mit offenen Rechnungen abgleichen (Erststufe, manueller CSV-Import).
        Gutschriften werden automatisch offenen Rechnungen zugeordnet, die Verbuchung erfolgt erst nach Ihrer Bestätigung.
      </p>

      {error && (
        <div className="bankrec-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Schließen</button>
        </div>
      )}
      {successMessage && (
        <div className="bankrec-success">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>Schließen</button>
        </div>
      )}

      <div className="bankrec-import-card">
        <div className="bankrec-import-row">
          <label className="bankrec-label" htmlFor="bankrec-file">CSV-Datei (z.B. Sparkassen-Export)</label>
          <input
            id="bankrec-file"
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
          />
        </div>

        <div className="bankrec-import-row">
          <label className="bankrec-label" htmlFor="bankrec-text">Oder Kontoumsätze hier einfügen</label>
          <textarea
            id="bankrec-text"
            rows={6}
            placeholder={'Buchungstag;Verwendungszweck;Beguenstigter/Zahlungspflichtiger;Betrag\n05.08.2026;RE-2026-00042 Floßfahrt;Max Mustermann;1.234,56'}
            value={csvText}
            onChange={(event) => setCsvText(event.target.value)}
          />
        </div>

        <div className="bankrec-import-actions">
          <button
            className="bankrec-btn-primary"
            onClick={handleReconcile}
            disabled={loading}
          >
            {loading ? 'Abgleich läuft ...' : 'Abgleichen'}
          </button>
        </div>
      </div>

      {result && (
        <>
          <div className="bankrec-summary">
            <span><strong>{result.summary.totalTransactions}</strong> Umsätze</span>
            <span><strong>{result.summary.creditTransactions}</strong> Gutschriften</span>
            <span><strong>{result.summary.debitTransactions}</strong> Lastschriften (ignoriert)</span>
            <span className="bankrec-summary-high"><strong>{result.summary.highConfidence}</strong> hoch</span>
            <span className="bankrec-summary-medium"><strong>{result.summary.mediumConfidence}</strong> mittel</span>
            <span className="bankrec-summary-low"><strong>{result.summary.lowConfidence}</strong> niedrig</span>
            <span className="bankrec-summary-none"><strong>{result.summary.noMatch}</strong> ohne Treffer</span>
          </div>

          <div className="bankrec-table-wrapper">
            <table className="bankrec-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th className="bankrec-col-amount">Betrag</th>
                  <th>Verwendungszweck</th>
                  <th>Name</th>
                  <th>Erkannte Rechnung</th>
                  <th>Konfidenz</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((row, index) => {
                  const isConfirmed = confirmedRows.has(index);
                  return (
                    <tr key={index} className={isConfirmed ? 'bankrec-row-done' : row.transaction.isCredit ? '' : 'bankrec-row-debit'}>
                      <td>{formatDate(row.transaction.date)}</td>
                      <td className={`bankrec-col-amount ${row.transaction.isCredit ? 'bankrec-amount-credit' : 'bankrec-amount-debit'}`}>
                        {formatCurrency(row.transaction.amount)}
                      </td>
                      <td title={row.transaction.purpose}>{truncate(row.transaction.purpose, 50)}</td>
                      <td title={row.transaction.name}>{truncate(row.transaction.name, 30)}</td>
                      <td>
                        {row.suggestion ? (
                          <div className="bankrec-suggestion" title={row.reason}>
                            <div className="bankrec-suggestion-number">{row.suggestion.invoiceNumber}</div>
                            <div className="bankrec-suggestion-details">
                              {row.suggestion.customerName || 'Kunde unbekannt'}, offen: {formatCurrency(row.suggestion.amountDue)}
                            </div>
                          </div>
                        ) : (
                          <span className="bankrec-no-suggestion" title={row.reason}>-</span>
                        )}
                      </td>
                      <td>
                        <span className={confidenceBadgeClass(row.confidence)} title={row.reason}>
                          {confidenceLabel(row)}
                        </span>
                      </td>
                      <td>
                        {isConfirmed ? (
                          <span className="bankrec-done">Verbucht</span>
                        ) : row.suggestion ? (
                          <button
                            className="bankrec-btn-secondary"
                            onClick={() => handleConfirmPayment(index, row)}
                            disabled={confirmingRow !== null}
                          >
                            {confirmingRow === index ? 'Wird verbucht ...' : 'Zahlung verbuchen'}
                          </button>
                        ) : row.transaction.isCredit && openInvoices && openInvoices.length > 0 ? (
                          /* Stufe 2: manuelle Zuordnung bei "kein Treffer" */
                          <div className="bankrec-manual">
                            <select
                              className="bankrec-manual-select"
                              value={manualSelection[index] ?? ''}
                              onChange={(event) => {
                                const value = parseInt(event.target.value, 10);
                                setManualSelection((previous) => ({ ...previous, [index]: value }));
                              }}
                            >
                              <option value="">Rechnung wählen ...</option>
                              {openInvoices.map((inv) => (
                                <option key={inv.invoiceId} value={inv.invoiceId}>
                                  {inv.invoiceNumber} | {truncate(inv.customerName || 'Kunde unbekannt', 22)} | offen {formatCurrency(inv.amountDue)}
                                </option>
                              ))}
                            </select>
                            <button
                              className="bankrec-btn-secondary"
                              onClick={() => {
                                const selectedId = manualSelection[index];
                                const invoice = openInvoices.find((inv) => inv.invoiceId === selectedId);
                                if (invoice) handleConfirmPayment(index, row, invoice);
                              }}
                              disabled={confirmingRow !== null || !manualSelection[index]}
                            >
                              {confirmingRow === index ? 'Wird verbucht ...' : 'Zuordnen'}
                            </button>
                          </div>
                        ) : (
                          <span className="bankrec-no-action">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BankReconciliation;
